import { DocumentOrganizationKeys, GroupSettings } from '@griply/types';
import {
  buildGoalReorderUpdate,
  buildGoalTaskDefaultFilter,
  buildGoalTaskFilterDocument,
  getGoalTaskListGoals,
  goalTaskListGroupOptions,
  isDefaultFilterDocument,
  isGoalArchived,
} from '@griply/utils';
import {
  useGoalById,
  useGoals,
  useRenameGoalSection,
  useSubGoals,
  useUpdateGoal,
} from 'features/goals';
import { useSortedTags } from 'features/tags';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  rootId,
  VirtualizedTreeListProvider,
} from 'shared/components/ui/virtualized-tree-list';
import {
  TaskListSearchProvider,
  useTaskListSearch,
} from 'shared/contexts/task-list-search';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { Paths } from 'shared/routes/paths';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { RelationshipUpdate } from 'shared/types/relationship';
import { GroupByMode } from 'shared/types/sorting-mode';
import { UpdateTask } from 'shared/types/task';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { noGroupingId, TaskFilterList } from 'shared/views/task-filter-list';

import { useGoalCustomOrderByGrouping } from '../../hooks/use-goal-custom-order-by-grouping';
import { OutletContext } from '../../outlet-context';
import { ArchivedItemsList } from '../archived-items-list';
import { Interactions } from './interactions';
import * as Styled from './sub-items-section.style';

const noLifeAreas: LifeArea[] = [];

export type SubItemsSectionProps = {
  fullSize?: boolean;
  grow?: boolean;
  hasLink?: boolean;
};

const SubItemsSectionContent: React.FC<SubItemsSectionProps> = ({
  fullSize,
  grow,
  hasLink,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { mainScrollRef } = useOutletContext<OutletContext>();
  const goal = useGoalById(id);
  const goals = useGoals();
  const tags = useSortedTags();
  const subGoals = useSubGoals(id, true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { searchQuery } = useTaskListSearch();

  const { submit: updateGoal } = useUpdateGoal();
  const renameGoalSection = useRenameGoalSection();
  const { submit: updateTasks } = useUpdateTasks();
  const updateRelationships = useUpdateRelationshipsReferences();

  const progressUrl = useMemo(
    () => replaceUrlParams(Paths.GoalDetailTasks, { id: id! }),
    [id],
  );

  const subGoalIds = useMemo(() => subGoals.map((g) => g.id), [subGoals]);

  // Archiving a goal also stamps archivedAt on its own tasks — both exclusions must lift.
  const isArchived = useMemo(
    () => !!goal && isGoalArchived(goal, goals),
    [goal, goals],
  );

  const filterDocument = useMemo(
    () => buildGoalTaskFilterDocument(goal, subGoalIds),
    [goal, subGoalIds],
  );

  const hasActiveFilters = useMemo(() => {
    if (searchQuery.trim()) {
      return true;
    }
    const defaults = buildGoalTaskDefaultFilter(goal, subGoalIds);
    return !isDefaultFilterDocument(
      {
        ...filterDocument,
        [DocumentOrganizationKeys.GroupByMode]:
          defaults[DocumentOrganizationKeys.GroupByMode],
        [DocumentOrganizationKeys.SortingMode]:
          defaults[DocumentOrganizationKeys.SortingMode],
      },
      defaults,
    );
  }, [filterDocument, goal, searchQuery, subGoalIds]);

  const filterGoals = useMemo(
    () =>
      getGoalTaskListGoals(goal, subGoals, {
        isGoalGrouping: filterDocument.groupByMode === GroupByMode.Goal,
      }),
    [filterDocument.groupByMode, goal, subGoals],
  );

  const groupSettings = useMemo<GroupSettings>(
    () => ({
      [GroupByMode.Section]: {
        sections: goal?.sections,
        onUpdateSection: (sectionId: ID, name: string) =>
          goal ? renameGoalSection(goal, sectionId, name) : undefined,
        goalId: goal?.id,
        showEmptyGroups: !hasActiveFilters,
      },
      [GroupByMode.Goal]: {
        goalId: goal?.id,
        showEmptyGroups: !hasActiveFilters,
        hideNoGoalGroup: true,
      },
      [GroupByMode.Priority]: { goalId: goal?.id },
      [GroupByMode.Type]: { goalId: goal?.id },
      [GroupByMode.Deadline]: { goalId: goal?.id },
      [GroupByMode.DeadlineDeadline]: { goalId: goal?.id },
      [GroupByMode.Tag]: { goalId: goal?.id },
      [GroupByMode.None]: { goalId: goal?.id },
    }),
    [goal, hasActiveFilters, renameGoalSection],
  );

  const onReorder = useCallback(
    (
      updatedSections: { id: ID; taskSorting: ID[] }[],
      updatedTasks: UpdateTask[],
      relationshipUpdates?: RelationshipUpdate[],
    ) => {
      if (!goal) {
        return;
      }

      updateTasks(updatedTasks);

      const groupBy =
        filterDocument[DocumentOrganizationKeys.GroupByMode] ??
        GroupByMode.Section;

      buildGoalReorderUpdate({
        goal,
        subGoals,
        updatedSections,
        groupBy,
        rootSectionId: rootId,
        noGroupingSectionId: noGroupingId,
      }).forEach((update) => updateGoal(update));

      if (relationshipUpdates?.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [
      filterDocument,
      goal,
      updateGoal,
      updateRelationships,
      updateTasks,
      subGoals,
    ],
  );

  const onSectionCollapse = useCallback(
    (collapsedSections: ID[]) => {
      if (!goal) {
        return;
      }

      updateGoal({
        id: goal.id,
        filterDocument: {
          ...(goal.filterDocument ?? {}),
          collapsedSections,
        },
      });
    },
    [goal, updateGoal],
  );

  const onUpdateFilterDocument = useCallback(
    (newFilterDocument: typeof filterDocument) => {
      if (!goal) {
        return;
      }
      updateGoal({
        id: goal.id,
        filterDocument: newFilterDocument,
      });
    },
    [goal, updateGoal],
  );

  const onResetFilter = useCallback(() => {
    if (!goal) {
      return;
    }
    updateGoal({
      id: goal.id,
      filterDocument: buildGoalTaskDefaultFilter(goal, subGoalIds),
    });
  }, [goal, subGoalIds, updateGoal]);

  const customOrder = useGoalCustomOrderByGrouping(
    filterDocument.groupByMode,
    goal,
    subGoals,
  );

  if (!goal) {
    return null;
  }

  return (
    <VirtualizedTreeListProvider>
      <PageSectionBase
        title={t('pages.goal-detail.tasks.title')}
        url={hasLink ? progressUrl : undefined}
        fullSize={fullSize}
        grow={grow}
        interactions={
          <Interactions
            filterDocument={filterDocument}
            defaultFilterDocument={buildGoalTaskDefaultFilter(goal, subGoalIds)}
            updateFilterDocument={onUpdateFilterDocument}
            onReset={onResetFilter}
            goals={filterGoals}
            tags={tags}
            lifeAreas={noLifeAreas}
            supportedGroupByModes={[...goalTaskListGroupOptions]}
            hideNoGoal
          />
        }
        noPadding
      >
        <Styled.ScrollContainer
          ref={scrollContainerRef}
          $scrollAuto={!!fullSize}
        >
          <TaskFilterList
            filterDocument={filterDocument}
            defaultFilterDocument={buildGoalTaskDefaultFilter(goal, subGoalIds)}
            customOrder={customOrder}
            onReorder={onReorder}
            onSectionCollapse={onSectionCollapse}
            scrollElement={
              fullSize
                ? scrollContainerRef
                : mainScrollRef ?? scrollContainerRef
            }
            groupSettings={groupSettings}
            onReset={onResetFilter}
            goals={filterGoals}
            canReorderGoals
            searchQuery={searchQuery}
            includeArchivedGoals={isArchived}
            includeArchived={isArchived}
          />

          <ArchivedItemsList goal={goal} />
        </Styled.ScrollContainer>
      </PageSectionBase>
    </VirtualizedTreeListProvider>
  );
};

export const SubItemsSection: React.FC<SubItemsSectionProps> = (props) => (
  <TaskListSearchProvider>
    <SubItemsSectionContent {...props} />
  </TaskListSearchProvider>
);
